import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { search, suggestions, type Result } from '../search/search';
import { flags } from '../state/flags';
import * as st from '../state/store';
import { LAYER_BY_ID } from '../viewer/layers';
import { track, resultBucket } from '../analytics';

export function SearchBox() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const results: Result[] = useMemo(() => (q.trim() ? search(q).filter((r) => r.entry.body !== 'f' || flags.femaleBody) : []), [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !(e.target as HTMLElement).closest('input, textarea')) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  // A search is counted once it settles (800 ms) or when a result is chosen sooner, whichever comes first.
  const logged = useRef('');
  const logSearch = () => {
    const query = q.trim().toLowerCase();
    if (!query || logged.current === query) return;
    logged.current = query;
    if (results.length) track('search', { result_count: resultBucket(results.length), matched_alias: !!results[0]?.via });
    else track('search_no_result', { query: query.slice(0, 40) });
  };
  useEffect(() => {
    if (!q.trim()) return;
    const t = setTimeout(logSearch, 800);
    return () => clearTimeout(t);
    // logSearch reads the current q and results, which are the dependencies here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, results]);

  const choose = (r: Result) => {
    logSearch();
    st.select(r.entry.id);
    track('card_open', { structure_id: r.entry.id, source: 'search' });
    setQ('');
    setOpen(false);
    input.current?.blur();
  };

  return (
    <div class="search" role="search">
      <label for="search-input" class="visually-hidden">Search the body</label>
      <input
        id="search-input"
        ref={input}
        type="search"
        autoComplete="off"
        placeholder="Search: “funny bone”, “heart”…"
        value={q}
        role="combobox"
        aria-expanded={open && !!q}
        aria-controls="search-results"
        aria-activedescendant={results[active] ? `sr-${results[active].entry.id}` : undefined}
        data-testid="search-input"
        onInput={(e) => {
          setQ((e.target as HTMLInputElement).value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, results.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && results[active]) choose(results[active]);
          else if (e.key === 'Escape') {
            setQ('');
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      {open && q.trim() && (
        <ul id="search-results" class="search-results" role="listbox" data-testid="search-results">
          {results.map((r, i) => (
            <li
              id={`sr-${r.entry.id}`}
              key={r.entry.id}
              role="option"
              aria-selected={i === active}
              class={i === active ? 'active' : ''}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(r);
              }}
              data-testid="search-result"
              data-id={r.entry.id}
            >
              <span class="sr-title">{r.via ? `${r.via} → ${r.entry.title}` : r.entry.title}</span>
              {r.entry.name !== r.entry.title && <span class="sr-name">{r.entry.name}</span>}
              <span class="badge" style={{ '--c': LAYER_BY_ID[r.entry.layer].color }}>{LAYER_BY_ID[r.entry.layer].label}</span>
            </li>
          ))}
          {!results.length && (
            <li class="no-results" role="option" aria-selected={false} data-testid="no-results">
              No match. Try{' '}
              {suggestions().map((s, i) => (
                <span key={s.id}>
                  {i ? ', ' : ''}
                  <button type="button" class="link" onMouseDown={(e) => { e.preventDefault(); choose({ entry: s, via: null, score: 0 }); }}>{s.title}</button>
                </span>
              ))}
              .
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
